import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Custom hook for managing refinance mode state and logic
 */
export const useRefinanceMode = (deals, getAuthHeaders, initialFormData, setFormData, setDialogOpen, navigate) => {
  const [refinanceMode, setRefinanceMode] = useState(false);
  const [refinanceFromDeal, setRefinanceFromDeal] = useState(null);
  const [refiIncludeFees, setRefiIncludeFees] = useState(true);
  const [refinanceSyndications, setRefinanceSyndications] = useState([]);

  /**
   * Initialize refinance mode from URL params
   */
  const initFromUrlParam = useCallback((refinanceFromId) => {
    if (!refinanceFromId || deals.length === 0 || refinanceMode || refinanceFromDeal) {
      return false;
    }

    // Try to find deal by UUID first, then by deal_id number
    let dealToRefinance = deals.find(d => d.id === refinanceFromId);
    if (!dealToRefinance) {
      dealToRefinance = deals.find(d => String(d.deal_id) === refinanceFromId);
    }

    if (!dealToRefinance) {
      return false;
    }

    setRefinanceFromDeal(dealToRefinance);
    setRefinanceMode(true);
    setRefiIncludeFees(true);

    // Calculate balance values
    const balanceWithFees = dealToRefinance.with_fees || dealToRefinance.balance || 0;
    const fundingAmt = parseFloat(dealToRefinance.funding_amount) || 0;
    const fees = parseFloat(dealToRefinance.fees) || 0;

    // Set form data for refinance
    setFormData(prev => ({
      ...prev,
      // Core deal info
      company_id: dealToRefinance.company_id || '',
      broker_id: dealToRefinance.broker_id || '',
      funder_id: dealToRefinance.funder_id || '',
      funder_percentage: dealToRefinance.funder_percentage?.toString() || '100',
      deal_shortcode: dealToRefinance.deal_shortcode || '',
      deal_name: dealToRefinance.deal_name || '',
      
      // Contact/Owner information
      legal_business_name: dealToRefinance.legal_business_name || dealToRefinance.deal_name || '',
      ein: dealToRefinance.ein || '',
      business_type: dealToRefinance.business_type || '',
      business_start_date: dealToRefinance.business_start_date || '',
      years_in_business: dealToRefinance.years_in_business?.toString() || '',
      owner_name: dealToRefinance.owner_name || '',
      owner_ssn: dealToRefinance.owner_ssn || '',
      owner_dob: dealToRefinance.owner_dob?.split('T')[0] || '',
      owner_address: dealToRefinance.owner_address || '',
      owner_phone: dealToRefinance.owner_phone || '',
      owner_email: dealToRefinance.owner_email || '',
      business_address: dealToRefinance.business_address || '',
      monthly_revenue: dealToRefinance.monthly_revenue?.toString() || '',
      
      // Commission info
      broker_commission: dealToRefinance.broker_commission?.toString() || '0',
      commission_rate: dealToRefinance.commission_rate?.toString() || '',
      
      // Refinance specific
      refi: true,
      refi_balance_payoff: balanceWithFees.toString(),
      deposit_amount: (fundingAmt - fees - balanceWithFees).toFixed(2)
    }));

    setDialogOpen(true);

    // Fetch syndications from the original deal
    axios.get(`${API}/syndications?deal_id=${dealToRefinance.id}`, { 
      headers: getAuthHeaders() 
    }).then(response => {
      setRefinanceSyndications(response.data || []);
    }).catch(err => {
      console.error('Failed to fetch syndications for refinance:', err);
      setRefinanceSyndications([]);
    });

    return true;
  }, [deals, refinanceMode, refinanceFromDeal, getAuthHeaders, setFormData, setDialogOpen]);

  /**
   * Reset refinance mode
   */
  const resetRefinanceMode = useCallback(() => {
    setRefinanceMode(false);
    setRefinanceFromDeal(null);
    setRefinanceSyndications([]);
    setRefiIncludeFees(true);
  }, []);

  /**
   * Handle refinance submission
   */
  const handleRefinanceSubmit = useCallback(async (payload) => {
    if (!refinanceFromDeal) {
      throw new Error('No deal selected for refinance');
    }

    const response = await axios.post(
      `${API}/deals/${refinanceFromDeal.id}/refinance`, 
      payload, 
      { headers: getAuthHeaders() }
    );
    
    const newDealId = response.data.new_deal_id;
    
    // Copy syndications to the new deal
    if (refinanceSyndications.length > 0) {
      let syndicationsCopied = 0;
      for (const syn of refinanceSyndications) {
        try {
          await axios.post(`${API}/syndications`, {
            deal_id: newDealId,
            syndicator_id: syn.syndicator_id,
            ownership_percentage: syn.ownership_percentage,
            collect_fees: syn.collect_fees || false
          }, { headers: getAuthHeaders() });
          syndicationsCopied++;
        } catch (synErr) {
          console.error(`Failed to copy syndication for ${syn.syndicator_name}:`, synErr);
        }
      }
      return { newDealId, syndicationsCopied, message: response.data.message };
    }
    
    return { newDealId, syndicationsCopied: 0, message: response.data.message };
  }, [refinanceFromDeal, refinanceSyndications, getAuthHeaders]);

  /**
   * Update refi balance payoff based on include fees toggle
   */
  const updateRefiBalance = useCallback((includeFees) => {
    if (!refinanceFromDeal) return;
    
    setRefiIncludeFees(includeFees);
    
    const balanceWithFees = refinanceFromDeal.with_fees || refinanceFromDeal.balance || 0;
    const balanceWithoutFees = refinanceFromDeal.balance || 0;
    const fundingAmt = parseFloat(refinanceFromDeal.funding_amount) || 0;
    const fees = parseFloat(refinanceFromDeal.fees) || 0;
    
    const newBalance = includeFees ? balanceWithFees : balanceWithoutFees;
    
    setFormData(prev => ({
      ...prev,
      refi_balance_payoff: newBalance.toString(),
      deposit_amount: (fundingAmt - fees - newBalance).toFixed(2)
    }));
  }, [refinanceFromDeal, setFormData]);

  return {
    refinanceMode,
    setRefinanceMode,
    refinanceFromDeal,
    setRefinanceFromDeal,
    refiIncludeFees,
    setRefiIncludeFees,
    refinanceSyndications,
    setRefinanceSyndications,
    initFromUrlParam,
    resetRefinanceMode,
    handleRefinanceSubmit,
    updateRefiBalance,
  };
};

export default useRefinanceMode;
